import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";

const SettingsContext = createContext(null);

function hexToHsl(hex) {
  if (!hex) return null;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, sat = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      default: hue = (r - g) / d + 4;
    }
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({
    firm_name: "PRO ACCOUNTANTS",
    tagline: "Chartered Certified Accountants, London",
    primary_color: "#0F172A",
    accent_color: "#059669",
  });

  const applyColors = useCallback((s) => {
    const p = hexToHsl(s.primary_color);
    const a = hexToHsl(s.accent_color);
    if (p) {
      document.documentElement.style.setProperty("--brand", p);
      document.documentElement.style.setProperty("--primary", p);
      document.documentElement.style.setProperty("--ring", p);
    }
    if (a) document.documentElement.style.setProperty("--brand-accent", a);
  }, []);

  const load = useCallback(async () => {
    const s = await api.branding();
    setSettings((prev) => ({ ...prev, ...s }));
    applyColors(s);
  }, [applyColors]);

  const save = useCallback(async (updates) => {
    const s = await api.updateSettings(updates);
    setSettings(s);
    applyColors(s);
    return s;
  }, [applyColors]);

  useEffect(() => { load(); }, [load]);

  const value = useMemo(() => ({ settings, save, reload: load }), [settings, save, load]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
